import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientService } from '@/api/services/client.service';
import { queryKeys } from '../query-keys';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import type { OfferFilters, UpdateOfferRequest, SubmitOfferRequest } from '@/api/types';

export const useOffers = (filters?: OfferFilters, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.offers.list(filters),
    queryFn: () => clientService.getOffers(filters),
    staleTime: 2 * 60 * 1000,
    enabled: options?.enabled !== false,
    retry: 1,
    retryDelay: 1000,
  });
};

export const useSubmitOffer = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: SubmitOfferRequest) => clientService.submitOffer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.offers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.client() });
      toast({
        title: t('offers.submitted') || 'تم تقديم العرض',
        description: t('offers.submittedDesc') || 'تم تقديم العرض بنجاح',
      });
    },
    onError: (error: any) => {
      const responseMessage = error?.response?.data?.error?.message || error?.response?.data?.message;
      toast({
        title: t('offers.submitFailed') || 'فشل تقديم العرض',
        description: responseMessage || (t('offers.submitFailedDesc') || 'حدث خطأ غير متوقع'),
        variant: 'destructive',
      });
    },
  });
};

export const useOffer = (id: number) => {
  return useQuery({
    queryKey: queryKeys.offers.detail(id),
    queryFn: () => clientService.getOfferDetails(id),
    enabled: !!id,
  });
};

export const useUpdateOffer = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateOfferRequest }) =>
      clientService.updateOffer(id, data),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.offers.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.offers.detail(variables.id),
      });
      toast({
        title: t('offers.updated') || 'تم تحديث العرض',
        description: t('offers.updatedDesc') || 'تم تحديث العرض بنجاح',
      });
    },
    onError: (error: any) => {
      const responseMessage = error?.response?.data?.error?.message || error?.response?.data?.message;
      toast({
        title: t('offers.updateFailed') || 'فشل التحديث',
        description: responseMessage || (t('offers.updateFailedDesc') || 'حدث خطأ غير متوقع'),
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteOffer = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (id: number) => clientService.deleteOffer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.offers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.client() });
      toast({
        title: t('offers.deleted') || 'تم حذف العرض',
        description: t('offers.deletedDesc') || 'تم حذف العرض بنجاح',
      });
    },
    onError: (error: any) => {
      const responseMessage = error?.response?.data?.error?.message || error?.response?.data?.message;
      toast({
        title: t('offers.deleteFailed') || 'فشل الحذف',
        description: responseMessage || (t('offers.deleteFailedDesc') || 'حدث خطأ غير متوقع'),
        variant: 'destructive',
      });
    },
  });
};
